// ============================================================
// TERROR TCG — RESPONSE BRIDGE
// ============================================================
// The nervous system of the RESPONSE WINDOW (Rulez v2 amendment,
// July 2026): "No declared action resolves while your adversary
// still holds an answer to it."
//
// WHY THIS FILE EXISTS
// The NPC engine (NPC.js) is driven from App.js — a separate React
// tree from Shatterfield. They share only Firebase. This module is
// the one thing BOTH import, so neither ever imports the other:
// zero circular-import risk.
//
// THE TWO LANES (Wave 2, July 2026 — the window became a MIRROR)
//
//   LANE 1 — RESPONDER (Wave 1): the NPC declares, the PLAYER answers.
//     1. Shatterfield (NPC matches only) registers a responder on
//        mount and unregisters on unmount.
//     2. NPC.js calls requestWindow(action) BEFORE resolving any
//        response-worthy action:
//          { kind: "LYT_ATTACK",      targetIdx, lyt }
//          { kind: "ANIMA_ATTACK",    lyt }
//          { kind: "LYT_ATTACK_TOME", lyt }
//          { kind: "ACTIVATION",      cardId }
//     3. The responder (Shatterfield) decides whether a window even
//        MATERIALIZES — only when the defender holds a legal,
//        affordable reaction RIGHT NOW. This is the 4xS anti-prompt-
//        fest law: option C on paper (every action passes through a
//        window), zero dead prompts in practice.
//
//   LANE 2 — REACTOR (Wave 2): the PLAYER declares, the NPC answers.
//     1. App.js registers the NPC's reactive brain (NPC.js
//        makeNpcReactor) when an NPC match starts and unregisters
//        when it ends.
//     2. Shatterfield calls requestReaction(action) BEFORE resolving
//        any response-worthy PLAYER action — same action shapes as
//        Lane 1, plus:
//          { kind: "REBUTTAL", cardId, level, chainDepth }  — a true
//            counter is itself an activation; the adversary may answer
//            it (chain law: same-or-higher level, never late), and
//            that answer may be answered, LIFO and unbounded (C12,
//            August 2026 — the chain session). `level` is the level
//            the answer must match or beat; `chainDepth` is display
//            only. The stack itself is held by whoever opened it —
//            Shatterfield's runChain — never by this module.
//     3. The reactor (NPC.js) reads fresh Firebase state, gates the
//        decision by skill + temperament, pays its own fares, moves
//        its own cards, and returns a verdict. No React, no closures.
//
//   PvP (Wave 3) does NOT flow through this module: both clients run
//   Shatterfield, so declaration travels through Firebase instead
//   (pendingAction / pendingResponse on the match node — see
//   Shatterfield's declarePvP). The bridge is the NPC's transport;
//   Firebase is the human's. One window system, two transports —
//   and since C12 one CHAIN, identical on both: a REBUTTAL is just
//   another declaration, so neither transport needed a new shape.
//
// RESOLUTION PAYLOAD (identical on both lanes)
//   { responded: false }                          — no window/reaction,
//     passed, snoozed, or timed out. Action resolves clean.
//   { responded: true, counter: null }            — a generic
//     interrupt fired (defensive instant, buff, flip). The
//     action STILL RESOLVES, but against FRESH state — the caller
//     re-reads before resolving. Reactor may add `reactionName`.
//   { responded: true, counter: "nihil"|"omnia",
//     late, counterName, counterCardId }          — a true
//     counter fired vs an ACTIVATION. Nihil = effect voided;
//     Omnia = the activated card is ruined. The fare is NOT
//     REFUNDED — which is a different statement from R105's
//     'paid every time', and this line used to be phrased in a
//     way that read like the retired 'paid once' rule.
//     COUNTER HIERARCHY (caP'n ruling, July 2026): late:true
//     means a L1/L2 counter answered a L3+ activation — it
//     cannot pre-empt. The effect resolves FIRST, then the
//     activated card must leave the field. L1 stops L2 fine;
//     only Level 3 or greater cards get the shield.
//
// SAFETY
//   • No responder/reactor registered (menus, tutorial, unmount) →
//     resolves immediately. Zero pause, zero cost.
//   • A hard failsafe timer guarantees neither side can EVER
//     soft-lock on a vanished window (screen exit, crash, unmount
//     mid-window). The window UI's own 5s countdown resolves first
//     in all healthy flows — the failsafe is the seatbelt.
// ============================================================

const NO_RESPONSE = Object.freeze({ responded: false });

// Hard ceiling — generous past the UI's 5s so slow devices never
// get their window yanked, tight enough that a dead window can't
// hold either warlock hostage.
const FAILSAFE_MS = 15000;

let responder = null;
let reactor = null;

// ── C12: THE FAILSAFE IS A WATCHDOG, NOT A STOPWATCH ─────────
// A chain lives entirely INSIDE one declaration: the player's 5s
// window, the adversary's beat of deliberation, another window, and
// down it goes. A flat 15s stopwatch would yank a live chain out from
// under a player still choosing — and the card they then fired would
// be paid for and MOVED while the declarer had already resolved
// without it. So any chain progress refreshes every live failsafe:
// it now fires after FAILSAFE_MS of genuine SILENCE, which is the only
// thing it was ever meant to catch (a vanished window, a crashed
// screen, an unmount mid-flight). Nothing hangs forever; a chain just
// has to keep visibly moving to keep its seat.
let lastActivityAt = 0;
export const noteWindowActivity = () => { lastActivityAt = Date.now(); };

// Shared race harness — one failsafe discipline for both lanes.
const raceSafe = async (fn, action, laneName) => {
  const startedAt = Date.now();
  let settled = false;
  try {
    const result = await Promise.race([
      Promise.resolve(fn(action)).finally(() => { settled = true; }),
      new Promise((res) => {
        const tick = () => {
          if (settled) return;                    // the lane answered — stand down
          const idle = Date.now() - Math.max(startedAt, lastActivityAt);
          if (idle >= FAILSAFE_MS) { res(NO_RESPONSE); return; }
          setTimeout(tick, Math.max(250, FAILSAFE_MS - idle));
        };
        setTimeout(tick, FAILSAFE_MS);
      }),
    ]);
    return result && typeof result === "object" ? result : NO_RESPONSE;
  } catch (e) {
    console.warn(`[ResponseBridge] ${laneName} threw — resolving clean:`, e?.message);
    return NO_RESPONSE;
  }
};

// ── LANE 1: RESPONDER (NPC declares → player answers) ────────

/**
 * Shatterfield registers its window-opener here (NPC matches only).
 * The registered fn receives the action and returns a Promise of a
 * resolution payload (see RESOLUTION PAYLOAD above).
 */
export const registerResponder = (fn) => {
  responder = typeof fn === "function" ? fn : null;
};

/** Shatterfield unregisters on unmount — requests flow free again. */
export const unregisterResponder = () => {
  responder = null;
};

/** True while a responder is live (debug/inspection convenience). */
export const hasResponder = () => !!responder;

/**
 * The NPC engine's declaration call. Declares an action and awaits
 * the player's answer (or the all-clear). NEVER throws, NEVER hangs.
 */
export const requestWindow = async (action) => {
  if (!responder) return NO_RESPONSE;
  return raceSafe(responder, action, "responder");
};

// ── LANE 2: REACTOR (player declares → NPC answers) ──────────

/**
 * App.js registers the NPC's reactive brain here (NPC matches only).
 * The registered fn receives the action and returns a Promise of a
 * resolution payload — same contract as the responder lane.
 */
export const registerReactor = (fn) => {
  reactor = typeof fn === "function" ? fn : null;
};

/** App.js unregisters on match exit — declarations flow free again. */
export const unregisterReactor = () => {
  reactor = null;
};

/** True while a reactor is live (debug/inspection convenience). */
export const hasReactor = () => !!reactor;

/**
 * Shatterfield's declaration call (NPC matches). Declares a PLAYER
 * action and awaits the NPC's answer (or the all-clear).
 * NEVER throws, NEVER hangs.
 */
export const requestReaction = async (action) => {
  if (!reactor) return NO_RESPONSE;
  return raceSafe(reactor, action, "reactor");
};

export default {
  registerResponder, unregisterResponder, hasResponder, requestWindow,
  registerReactor, unregisterReactor, hasReactor, requestReaction,
  noteWindowActivity,
};
